import type { EProductType } from '@quesmed/types/models';
import type { IDropdownOptionsData } from '@quesmed/types/resolvers/query/admin';
import { DROPDOWN_OPTIONS, type IDropdownOptionsVar } from '@quesmed/types/resolvers/query/admin';
import { useMemo, useState } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { DropdownOptionsDocument } from '@/graphql/gql/graphql';
import '@/graphql/query/dropdown';
import { useQuery } from '@/libs';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  AutoCompleteMulti,
  type AutoCompleteProps,
  FormAutoCompleteMulti,
  type FormAutoCompleteMultiProps,
  type ISelectItem,
  type ISelectItemSection,
} from '../index';

interface AsyncSearchMultiSelectProps<T extends FieldValues>
  extends Omit<FormAutoCompleteMultiProps<T>, 'options'> {
  queryKey?:
    | 'concepts'
    | 'presentations'
    | 'conditions'
    | 'areas'
    | 'themes'
    | 'writers'
    | 'editors'
    | 'stationTopics';
  control: Control<T, unknown>;
  extraSections?: ISelectItemSection[];
  extraOptions?: ISelectItem[];
  groupBy?: 'options' | 'sections';
  entitlementIds?: (string | number)[];
  topicIds?: (string | number)[];
  conditionIds?: (string | number)[];
  presentationIds?: (string | number)[];
  skip?: boolean;
  addValuePrefix?: boolean;
  productId?: EProductType;
  apiSearch?: boolean;
}

interface AsyncNormalMultiSelectProps extends Omit<AutoCompleteProps, 'options'> {
  queryKey?: 'concepts' | 'presentations' | 'conditions' | 'themes' | 'writers' | 'editors';
  extraSections?: ISelectItemSection[];
  extraOptions?: ISelectItem[];
  groupBy?: 'options' | 'sections';
  entitlementIds?: (string | number)[];
  topicIds?: (string | number)[];
  addValuePrefix?: boolean;
  productId?: EProductType;
  apiSearch?: boolean;
}

export const AsyncSearchMultiSelect = <T extends FieldValues>({
  queryKey,
  control,
  extraSections,
  extraOptions,
  groupBy = 'sections',
  entitlementIds,
  topicIds,
  conditionIds,
  presentationIds,
  skip = false,
  addValuePrefix = true,
  productId,
  apiSearch = true,
  ...rest
}: AsyncSearchMultiSelectProps<T>) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 800);
  const { data, loading } = useQuery(DropdownOptionsDocument, {
    skip,
    variables: {
      force: false,
      search: apiSearch ? debouncedSearchText?.trim() : '',
      table: queryKey ?? 'presentations',
      entitlementIds: entitlementIds?.length ? entitlementIds.map(Number) : undefined,
      topicIds: topicIds?.length ? topicIds.map(Number) : undefined,
      conditionIds: conditionIds?.length ? conditionIds.map(Number) : undefined,
      presentationIds: presentationIds?.length ? presentationIds.map(Number) : undefined,
      productId: productId ? productId : undefined,
    },
  });

  const dropdownOptions = useMemo(
    () => (data?.admin?.dropdownOptions ?? []).filter((opt): opt is NonNullable<typeof opt> => opt != null),
    [data],
  );

  const sections = useMemo(() => {
    if (groupBy === 'options') {
      return [];
    }
    const groupedOptions = dropdownOptions.reduce((acc, opt) => {
      const groupId = opt.metaData?.value;
      if (!groupId) return acc;
      const groupName = opt.metaData?.key ?? '';
      const label = addValuePrefix ? `[${groupId}] ${groupName}` : groupName;

      const existingGroup = acc.find((group) => group.label === label);
      const item = { key: opt.value ?? '', label: opt.key ?? '' };

      if (existingGroup) {
        existingGroup.items.push(item);
      } else {
        acc.push({
          label,
          items: [item],
        });
      }

      return acc;
    }, [] as ISelectItemSection[]);

    return [...(extraSections ?? []), ...groupedOptions];
  }, [dropdownOptions, extraSections]);

  const options = useMemo(() => {
    if (groupBy === 'sections') {
      return [];
    }
    const filteredOptions = dropdownOptions.map((opt) => ({
      key: opt.value ?? '',
      label: opt.key ?? '',
      description: opt.description,
    }));

    return [...(extraOptions ?? []), ...filteredOptions];
  }, [dropdownOptions, extraOptions]);

  return (
    <FormAutoCompleteMulti
      {...rest}
      // isVirtualized
      options={groupBy === 'options' ? options : []}
      sections={groupBy === 'sections' ? sections : undefined}
      isLoading={loading}
      onInputChange={setSearchText}
      control={control}
    />
  );
};

export const AsyncNormalMultiSelect = ({
  queryKey,
  extraSections,
  extraOptions,
  groupBy = 'sections',
  entitlementIds,
  topicIds,
  addValuePrefix = true,
  productId,
  apiSearch = true,
  ...rest
}: AsyncNormalMultiSelectProps) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 800);
  const { data, loading } = useQuery<IDropdownOptionsData, IDropdownOptionsVar>(DROPDOWN_OPTIONS, {
    variables: {
      force: false,
      search: apiSearch ? debouncedSearchText?.trim() : '',
      table: queryKey ?? 'presentations',
      entitlementIds: entitlementIds?.length ? entitlementIds.map(Number) : undefined,
      topicIds: topicIds?.length ? topicIds.map(Number) : undefined,
      productId: productId ? productId : undefined,
    },
  });

  const sections = useMemo(() => {
    if (groupBy === 'options') {
      return [];
    }
    const opts = data?.admin?.dropdownOptions ?? [];
    const groupedOptions = opts.reduce((acc, opt) => {
      const groupId = opt.metaData?.value;
      const label = addValuePrefix ? `[${groupId}] ${opt.metaData.key}` : opt.metaData.key;
      if (!groupId) return acc;

      const existingGroup = acc.find((group) => group.label === label);
      const item = { key: opt.value as string, label: opt.key as string };

      if (existingGroup) {
        existingGroup.items.push(item);
      } else {
        acc.push({
          label,
          items: [item],
        });
      }

      return acc;
    }, [] as ISelectItemSection[]);

    return [...(extraSections ?? []), ...groupedOptions];
  }, [data, extraSections]);

  const options = useMemo(() => {
    if (groupBy === 'sections') {
      return [];
    }
    const opts = data?.admin?.dropdownOptions ?? [];

    const filteredOptions = opts.map((opt) => ({
      key: opt.value as string,
      label: opt.key,
      description: opt?.description,
    }));

    return [...(extraOptions ?? []), ...filteredOptions];
  }, [data, extraOptions]);

  return (
    <AutoCompleteMulti
      {...rest}
      // isVirtualized
      options={groupBy === 'options' ? options : []}
      sections={groupBy === 'sections' ? sections : undefined}
      isLoading={loading}
      onInputChange={setSearchText}
    />
  );
};
